import { Avatar, Button, Input, InputNumber, List, Spin, Switch, Tooltip, Typography, message } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  coreFetchCustomers,
  coreSetSelectedCustomers,
  fetchSelectedCustomers,
  setCreditLimit,
  setCustomerSelfApproval,
  setCustomerSelfApprovedOrderLimit
} from '../../Reducers/core'
import './styles.scss'
import EmailsList from './EmailsList'
const { Title } = Typography;

const hasSelfApprovedOrderLimit = (params) => (
  params?.selfApprovedOrderLimit !== null &&
  params?.selfApprovedOrderLimit !== undefined
);

const getSelfApproval = (params) => params?.selfApproval ?? false;

const getSelfApprovedOrderLimit = (params) => (
  hasSelfApprovedOrderLimit(params) ? params.selfApprovedOrderLimit : 1
);

function ManagerSettings() {

  const [enableChangeLimitId, setEnableChangeLimitId] = useState(null);
  const [newCreditLimit, setNewCreditLimit] = useState(0);
  const [customerParamOverrides, setCustomerParamOverrides] = useState({});
  const [updatingSelfApprovalId, setUpdatingSelfApprovalId] = useState(null);
  const [updatingSelfApprovedOrderLimitId, setUpdatingSelfApprovedOrderLimitId] = useState(null);

  const dispatch = useDispatch()

  const { payload, isLoading } = useSelector((state) => state.core.customers, shallowEqual)
  const selectedCustomersData = useSelector((state) => state.core.customersSettingsSelected, shallowEqual)
  const updateData = useSelector((state) => state.core.creditLimit, shallowEqual)

  const userId = selectedCustomersData?.payload && selectedCustomersData?.payload?.userId
  const selectedIsloading = selectedCustomersData?.isLoading
  const selectedCustomers = selectedCustomersData?.payload && selectedCustomersData?.payload?.selectedCustomers

  const updateDataIsLoading = updateData?.isLoading;
  const sortedCustomers = [...(payload || [])].sort((a, b) => {
    const nameCompare = (a.name ?? '').localeCompare(b.name ?? '');

    if (nameCompare !== 0) {
      return nameCompare;
    }

    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });

  const refreshCustomers = useCallback(() => {
    return dispatch(coreFetchCustomers()).catch(() => null);
  }, [dispatch]);

  const handleChangeUserToList = useCallback((e, id) => {
    let data
    
    if (selectedCustomers.length === 1 && e === false) {
      message.error(`Can't remove final customer, please select at least one!`)
    } else {

    if (selectedCustomers) {
      data = e ? [...selectedCustomers, id] : selectedCustomers.filter((customer) => customer != id);
    } else {
      data = [id]
    }

    const dataToSend = {userId, selectedCustomers: data};
    dispatch(coreSetSelectedCustomers(dataToSend))
    .then((res) => {
      message.success(`Successfully ${e ? "Added" : "Removed"} user!`)
    })
    .catch((e) => {
      message.error(`Failed to ${e ? "Add" : "Remove"} user! ${e}`)
    })
  }
  }, [dispatch, selectedCustomers, userId])

  const handleEnableChangingBalance = useCallback((e, item) => {
    if (!e) {
      setEnableChangeLimitId(null)
      setNewCreditLimit(null)
    } else {
      setEnableChangeLimitId(item.id);
      setNewCreditLimit(item.creditLimit)
    }
  })

  const handleSaveNewLimit = useCallback((id, status) => {

    var creditLimit = status === 'reset' ? 0 : +newCreditLimit

    dispatch(setCreditLimit({customerId: id, creditLimit, cur: "USD" }))
      .then((res) => {
        message.success(`Updated credit limit: ${creditLimit} €`);
      })
      .catch((e) => {
        message.error(`Failed to update credit limit: ${e}`);
      })
      .finally(() => {
        setEnableChangeLimitId(null);
        dispatch(coreFetchCustomers());
      })
  }, [newCreditLimit])

  const handleSelfApprovalChange = useCallback(async (selfApproval, item) => {
    const customerId = item.id;
    const params = item?.params;
    const previousOverride = customerParamOverrides[customerId];
    const customerHasSelfApprovedOrderLimit = hasSelfApprovedOrderLimit(params);

    setCustomerParamOverrides((prev) => ({
      ...prev,
      [customerId]: {
        ...(prev[customerId] || {}),
        selfApproval,
        ...(selfApproval && !customerHasSelfApprovedOrderLimit ? {
          selfApprovedOrderLimit: prev[customerId]?.selfApprovedOrderLimit ?? 1,
          showSelfApprovedOrderLimit: true,
        } : {}),
        ...(!selfApproval && !customerHasSelfApprovedOrderLimit ? {
          showSelfApprovedOrderLimit: false,
        } : {}),
      }
    }));

    setUpdatingSelfApprovalId(customerId);

    try {
      await dispatch(setCustomerSelfApproval({ customerId, selfApproval }));
      message.success('Successfully updated self approval!');
      await refreshCustomers();
    } catch (error) {
      setCustomerParamOverrides((prev) => {
        if (previousOverride === undefined) {
          const next = { ...prev };
          delete next[customerId];
          return next;
        }

        return {
          ...prev,
          [customerId]: previousOverride,
        };
      });
      message.error(`Failed to update self approval: ${error}`);
    } finally {
      setUpdatingSelfApprovalId(null);
    }
  }, [customerParamOverrides, dispatch, refreshCustomers]);

  const handleSelfApprovedOrderLimitChange = useCallback(async (value, item) => {
    const nextSelfApprovedOrderLimit = Number(value);

    if (
      !Number.isInteger(nextSelfApprovedOrderLimit) ||
      nextSelfApprovedOrderLimit < 1 ||
      nextSelfApprovedOrderLimit > 10
    ) {
      return;
    }

    const customerId = item.id;
    const params = item?.params;
    const previousOverride = customerParamOverrides[customerId];
    const currentSelfApprovedOrderLimit =
      previousOverride?.selfApprovedOrderLimit ?? getSelfApprovedOrderLimit(params);

    if (currentSelfApprovedOrderLimit === nextSelfApprovedOrderLimit) {
      return;
    }

    setCustomerParamOverrides((prev) => ({
      ...prev,
      [customerId]: {
        ...(prev[customerId] || {}),
        selfApprovedOrderLimit: nextSelfApprovedOrderLimit,
        showSelfApprovedOrderLimit: true,
      }
    }));

    setUpdatingSelfApprovedOrderLimitId(customerId);

    try {
      await dispatch(
        setCustomerSelfApprovedOrderLimit({
          customerId,
          selfApprovedOrderLimit: nextSelfApprovedOrderLimit
        })
      );
      message.success('Successfully updated self approved order limit!');
      await refreshCustomers();
    } catch (error) {
      setCustomerParamOverrides((prev) => {
        if (previousOverride === undefined) {
          const next = { ...prev };
          delete next[customerId];
          return next;
        }

        return {
          ...prev,
          [customerId]: previousOverride,
        };
      });
      message.error(`Failed to update self approved order limit: ${error}`);
    } finally {
      setUpdatingSelfApprovedOrderLimitId(null);
    }
  }, [customerParamOverrides, dispatch, refreshCustomers]);

  useEffect(() => {
    dispatch(fetchSelectedCustomers())
  }, [dispatch]);

  return (
    <>
    <Title>Managers Settings</Title>
    {selectedIsloading ? 
        <Spin /> 
        :
        <List
        grid={{gutter: 16, column: 4}}
        itemLayout="horizontal"
        dataSource={sortedCustomers}
        renderItem={(item, index) => {
          const isActiveUser = selectedCustomers?.includes(item.id)
          const customerParamOverride = customerParamOverrides[item.id];
          const selfApproval = customerParamOverride?.selfApproval ?? getSelfApproval(item?.params);
          const shouldShowSelfApprovedOrderLimit =
            customerParamOverride?.showSelfApprovedOrderLimit ?? hasSelfApprovedOrderLimit(item?.params);
          const selfApprovedOrderLimit =
            customerParamOverride?.selfApprovedOrderLimit ?? getSelfApprovedOrderLimit(item?.params);
          const isSelfApprovalUpdating = updatingSelfApprovalId === item.id;
          const isSelfApprovedOrderLimitUpdating = updatingSelfApprovedOrderLimitId === item.id;

          return (
          
          <List.Item>
            <List.Item.Meta
              // avatar={<Avatar src={`https://api.dicebear.com/7.x/miniavs/svg?seed=${index}`} />}
              title={<div className='user_avatar'><Avatar src={`https://api.dicebear.com/7.x/miniavs/svg?seed=${index}`} /><h4>{item.name}</h4></div>}
              description={
              <div className='user_info'>
              <div className='user_info__balance'>Balance: <b>{item.balance} €</b></div>
              <div className='user_info__credit'>
              <Input 
                size='small'
                type='number' 
                value={enableChangeLimitId !== item.id ? item.creditLimit : newCreditLimit} 
                onChange={(e) => setNewCreditLimit(e.target.value)}
                addonAfter='€' 
                addonBefore='Credit'
                disabled={enableChangeLimitId !== item.id} 
                loading={isLoading}
              />
              {enableChangeLimitId === item.id &&
              <>
              <Button 
                size='small'
                onClick={() => handleSaveNewLimit(item.id, 'set')}
                isLoading={updateDataIsLoading}
                >
                  Save
                </Button>
                <Button 
                size='small'
                onClick={() => handleSaveNewLimit(item.id, 'reset')}
                >
                  Reset
                </Button>
                </>
                }
              <Tooltip title={`change limit ${item.creditLimit > 0 ? 'to zero' : ''}`}>
              <Switch
                size="small"
                checked={enableChangeLimitId === item.id}
                onChange={(e) => handleEnableChangingBalance(e, item)}
              />
              
              </Tooltip>
              </div>
              <div className='user_info__active'>
              {isActiveUser ? 'Delete from list: ' : 'Add user to list: ' }
              <Switch
                defaultChecked={isActiveUser}
                onChange={(e) => handleChangeUserToList(e, item.id)}
              />
              </div>

              <div className='user_info__selfApproval'>
                <span>Self approval:</span>
                <Switch
                  size='small'
                  checked={selfApproval}
                  loading={isSelfApprovalUpdating}
                  disabled={isSelfApprovedOrderLimitUpdating}
                  onChange={(value) => handleSelfApprovalChange(value, item)}
                />
                {shouldShowSelfApprovedOrderLimit &&
                  <div className='user_info__selfApprovalLimit'>
                    <span>Limit:</span>
                    <InputNumber
                      size='small'
                      min={1}
                      max={10}
                      precision={0}
                      value={selfApprovedOrderLimit}
                      disabled={!selfApproval || isSelfApprovalUpdating || isSelfApprovedOrderLimitUpdating}
                      onChange={(value) => handleSelfApprovedOrderLimitChange(value, item)}
                    />
                  </div>
                }
              </div>

              <div className='settings_wrapper'>
              <EmailsList payload={payload} isLoading={isLoading} item={item} />
              </div>
              </div>}
            />
          </List.Item>
        )
        }}
      />}
  </>
  )
}

export default ManagerSettings
